import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';


const PerformanceAnalytics = () => {
  const metrics = [
    {
      id: 1,
      label: 'Total Sessions',
      value: '127',
      change: '+12%',
      trend: 'up',
      icon: 'Calendar',
      color: 'primary'
    },
    {
      id: 2,
      label: 'Completion Rate',
      value: '94.5%',
      change: '+2.3%',
      trend: 'up',
      icon: 'CheckCircle2',
      color: 'success'
    },
    {
      id: 3,
      label: 'Avg. Satisfaction',
      value: '4.8/5.0',
      change: '+0.2',
      trend: 'up',
      icon: 'Star',
      color: 'accent'
    },
    {
      id: 4,
      label: 'Response Time',
      value: '2.3 hrs',
      change: '-0.5 hrs',
      trend: 'up',
      icon: 'Clock',
      color: 'secondary'
    }
  ];

  const recentFeedback = [
    {
      id: 1,
      rating: 5,
      comment: 'Dr. Chen was incredibly understanding and provided practical strategies that I could implement immediately. The session felt safe and judgment-free.',
      seeker: 'Anonymous',
      date: '2025-12-03',
      topic: 'Workplace Stress'
    },
    {
      id: 2,
      rating: 5,
      comment: 'Having someone who truly understood what I was going through made all the difference. Thank you for sharing your experience and wisdom.',
      seeker: 'Anonymous',
      date: '2025-12-02',
      topic: 'Bereavement'
    },
    {
      id: 3,
      rating: 4,
      comment: 'Very helpful session. Would have appreciated more time to discuss coping mechanisms, but overall excellent support.',
      seeker: 'Anonymous',
      date: '2025-12-01',
      topic: 'Anxiety Management'
    }
  ];

  const bookingPatterns = [
    { day: 'Mon', sessions: 8 },
    { day: 'Tue', sessions: 12 },
    { day: 'Wed', sessions: 15 },
    { day: 'Thu', sessions: 10 },
    { day: 'Fri', sessions: 14 },
    { day: 'Sat', sessions: 6 },
    { day: 'Sun', sessions: 4 }
  ];

  const maxSessions = Math.max(...bookingPatterns?.map(p => p?.sessions));

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name={i < rating ? 'Star' : 'Star'}
        size={16}
        className={i < rating ? 'text-accent fill-accent' : 'text-muted-foreground'}
      />
    ));
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Performance Analytics</h2>
              <p className="text-sm text-muted-foreground">Track your impact and session insights</p>
            </div>
          </div>
          <Select
            options={[
              { value: '7d', label: 'Last 7 days' },
              { value: '30d', label: 'Last 30 days' },
              { value: '90d', label: 'Last 90 days' },
              { value: 'all', label: 'All time' }
            ]}
            value="30d"
            onChange={() => {}}
            className="w-40"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics?.map((metric) => (
            <div key={metric?.id} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg bg-${metric?.color}/10 flex items-center justify-center`}>
                  <Icon name={metric?.icon} size={20} className={`text-${metric?.color}`} />
                </div>
                <span className={`text-xs font-medium ${metric?.trend === 'up' ? 'text-success' : 'text-destructive'}`}>
                  {metric?.change}
                </span>
              </div>
              <p className="text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
              <p className="text-sm text-muted-foreground">{metric?.label}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-base font-semibold text-foreground mb-4">Weekly Booking Pattern</h3>
        <div className="flex items-end justify-between gap-2 h-48">
          {bookingPatterns?.map((pattern) => (
            <div key={pattern?.day} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full bg-muted rounded-t-lg relative" style={{ height: '100%' }}>
                <div
                  className="absolute bottom-0 w-full bg-primary rounded-t-lg transition-all duration-300"
                  style={{ height: `${(pattern?.sessions / maxSessions) * 100}%` }}
                />
              </div>
              <div className="text-center">
                <p className="text-xs font-medium text-foreground">{pattern?.sessions}</p>
                <p className="text-xs text-muted-foreground">{pattern?.day}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-foreground">Recent Feedback</h3>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export All
          </Button>
        </div>

        <div className="space-y-4">
          {recentFeedback?.map((feedback) => (
            <div key={feedback?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {renderStars(feedback?.rating)}
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(feedback.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <p className="text-sm text-foreground mb-3">{feedback?.comment}</p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="User" size={14} />
                  {feedback?.seeker}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Tag" size={14} />
                  {feedback?.topic}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Heart" size={20} className="text-warning mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">Workload Management</p>
            <p className="text-sm text-muted-foreground mt-1">
              You've completed 12 sessions this week. Consider taking breaks to prevent burnout. Your wellbeing matters too.
            </p>
            <Button variant="link" size="sm" className="mt-2 p-0 h-auto text-warning">
              View Wellness Resources
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalytics;